import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { Sheet, SheetGroup, SheetView } from '../types';
import {
  UnSavedSheetHistory,
  UnSavedSheetsChanges,
} from '../types/sheet/history';
import { SheetManagerSliceState } from './types';
import helpers from './sheetsManagerSliceHelpers';
import { RootState } from '../../core/redux/store';
import { createSheetDefaultView } from '../utils/views/views';

const initialState: SheetManagerSliceState = {
  selecting: false,
  activeSheetId: '',

  entityIdToNameMap: {
    sheetsIdToName: {},
    groupsIdToName: {},
  },

  isManualSave: false,

  sheets: {},
  sheetViews: {},
  staleSheets: {},
  history: {
    past: [],
    presentSheetViews: {},
    future: [],
  },
  groups: {},

  history2: {
    past: [],
    future: [],
  },

  changesToBeSaved: [],

  sheetGroups: {},

  sheetsIdToName: {},
  unSavedChanges: {
    groups: [],
    sheetsIdToName: [],
    sheetRoot: [],
    sheets: {},
  },
  unSavedHistory: [],
};

interface InitializeSheetsState
  extends Pick<SheetManagerSliceState, 'isManualSave' | 'entityIdToNameMap'> {
  unSavedHistory?: UnSavedSheetHistory[];
  unSavedChanges?: UnSavedSheetsChanges;
}

interface UpdateSheetView extends Partial<SheetView> {
  sheetId: string;
  selecting?: boolean;
}

const sheetsSlice = createSlice({
  name: 'sheetsManager',
  initialState,
  reducers: {
    initializeSheetsState: (
      state,
      action: PayloadAction<InitializeSheetsState>
    ) => {
      const { unSavedChanges, unSavedHistory } = action.payload;
      // state.history = action.payload.history;
      // state.history.presentSheetViews = {}
      if (unSavedChanges) state.unSavedChanges = unSavedChanges;
      if (unSavedHistory) state.unSavedHistory = unSavedHistory;

      state.isManualSave = !!action.payload.isManualSave;
      // state.groups = action.payload.groups;
      state.entityIdToNameMap = action.payload.entityIdToNameMap;
      // state.sheetsIdToName = action.payload.sheetsIdToName;
    },
    resetSheetsState: () => {
      return initialState;
    },
    addSheet: (state, action: PayloadAction<Sheet>) => {
      const sheet = action.payload;
      const sheetId = sheet.id;
      state.sheets[sheetId] = sheet;
      // if (!state.sheetViews[sheetId]) {
      //   // TODO: Create initial sheet views
      // }
    },
    addSheetsGroup: (state, action: PayloadAction<SheetGroup>) => {
      state.sheetGroups[action.payload.id] = action.payload;
    },
    updateSheetView: (state, action: PayloadAction<UpdateSheetView>) => {
      const { sheetId, selecting, ...sheetView } = action.payload;
      const sheet = state.sheets[sheetId];

      if (!state.sheetViews[sheetId])
        state.sheetViews[sheetId] = createSheetDefaultView();

      const view = state.sheetViews[sheetId];

      if (sheetView.activeCell) {
        view.activeCell = sheetView.activeCell;
      }
      if (sheetView.activeCellArea) {
        view.activeCellArea = sheetView.activeCellArea;
      }
      if (sheetView.selections) {
        view.selections = sheetView.selections;
      }
      // if (sheetView.viewport && sheet) {
      // view.viewport = sheetView.viewport;
      // const rowViewport = createVirtualViewport(
      //   view.viewport.rs,
      //   view.viewport.re,
      //   sheet.rows,
      //   sheet.defaultRowHeight,
      //   sheet.hiddenRows,
      //   sheet.zoom,
      //   sheet.freezedRows,
      //   sheet.filterResult
      // );
      // console.log("rowViewport --- from update")
      // }

      if (sheetView.rowViewport) {
        view.rowViewport = sheetView.rowViewport;
      }

      if (sheetView.colViewport) {
        view.colViewport = sheetView.colViewport;
      }

      if (selecting !== undefined) {
        state.selecting = !!selecting;
        if (selecting) state.activeSheetId = sheetId;
        else state.activeSheetId = '';
      }
      if (sheetView.temporarySelection)
        view.temporarySelection = sheetView.temporarySelection;
      else delete view.temporarySelection;
    },
    updateTabSharedChanges: helpers.updateTabSharedChanges,
    updateSheetManagerState: helpers.updateSheetManagerState,
    updateStateFromSheetsManagementSet:
      helpers.updateStateFromSheetsManagementSet,
    updateSheetsStateWithPreCalculatedHistory:
      helpers.updateSheetsStateWithPreCalculatedHistory,
    enableManualSave: (state) => {
      state.isManualSave = true;
    },
    disableManualSave: (state) => {
      state.isManualSave = false;
    },
    manualModeChangesDiscarded: helpers.manualModeChangesDiscarded,
    manualModeChangesSaved: helpers.manualModeChangesSaved,
    addSharedChanges: helpers.addSharedChanges,
    closeGroup: helpers.closeGroup,
  },
});

export const {
  updateTabSharedChanges,

  initializeSheetsState,
  resetSheetsState,
  
  addSheet,
  addSheetsGroup,

  updateSheetManagerState,
  updateSheetsStateWithPreCalculatedHistory,

  updateSheetView,

  enableManualSave,
  disableManualSave,
  manualModeChangesDiscarded,
  manualModeChangesSaved,
  addSharedChanges,

  closeGroup,
  updateStateFromSheetsManagementSet,
} = sheetsSlice.actions;

export default sheetsSlice.reducer;

export const getSheetsInAGroup = createSelector(
  (state: RootState, groupId: string) => ({
    sheetsIdToName: state.sheetsManager.sheetsIdToName,
    groups: state.sheetsManager.groups,
    groupId,
  }),
  ({ sheetsIdToName, groupId, groups }) => {
    const sheetNames: Array<{
      name: string;
      id: string;
    }> = [];
    let names: string[] = [];
    groups[groupId]?.sheets.forEach((sheetId) => {
      let name = sheetsIdToName[sheetId] ?? '';
      if (names.includes(name.toLowerCase())) {
        name = `${name} (${sheetId.substring(0, 4)})`;
      }
      names.push(name.toLowerCase());

      sheetNames.push({
        name,
        id: sheetId,
      });
    });

    return sheetNames;
  }
);

// export const getCellValue = ({
//   sheet,
//   position,
// }: {
//   sheet: Pick<Sheet, 'cols' | 'rows' | 'cells' | 'results'>;
//   position: CellPosition;
// }) => {
//   let visibleValue = '';
//   let isError = false;
//   if (!sheet)
//     return {
//       visibleValue,
//       isError,
//     };

//   const { cols, rows, cells, results } = sheet;
//   // const cell = getCellFromPosition(cols, rows, cells, position);
//   const cell: Cell | undefined = {};

//   const result = '';
//   // const result = getResultFromPosition(
//   //   cols,
//   //   rows,
//   //   results,
//   //   position
//   // )?.toString();

//   // console.log("result", result)

//   if (result !== undefined) {
//     if (result.startsWith('#')) {
//       const [err, _] = result.split(':');
//       visibleValue = err;
//       isError = true;
//     } else {
//       visibleValue = result;
//       // visibleValue = `${cell?.value?.toString() ?? ''} | ${result}`;
//     }
//   } else {
//     visibleValue = cell?.value?.toString() ?? '';
//   }

//   // visibleValue = cell?.value?.toString() ?? '';

//   return {
//     visibleValue,
//     isError,
//   };
// };

// export const selectCellValue = createSelector(
//   (state: RootState, sheetId: string, position: CellPosition) => ({
//     sheet: selectSheet(state, sheetId),
//     position,
//   }),
//   getCellValue
// );
